import React, { createContext, useContext, useState, useEffect } from 'react';
import { setAppDefaultTheme } from '../theme/themes';
import api from '../services/api';

const STORAGE_KEY = 'app_settings_cache';

const DEFAULT_FEATURES = { uutiset: true, saavutukset: true, viikkoraha: true };

const getInitialSettings = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.defaultTheme) {
        setAppDefaultTheme(parsed.defaultTheme);
      }
      return { ...parsed, features: parsed.features || DEFAULT_FEATURES, loaded: false };
    }
  } catch {}
  return {
    defaultTheme: '',
    iconUpdatedAt: null,
    shortName: 'Kotityöt',
    requireTaskConfirm: false,
    features: DEFAULT_FEATURES,
    loaded: false,
  };
};

const AppSettingsContext = createContext();

export const useAppSettings = () => {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error('useAppSettings must be used within AppSettingsProvider');
  }
  return context;
};

export const AppSettingsProvider = ({ children }) => {
  const [appSettings, setAppSettings] = useState(getInitialSettings);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/admin/pwa-settings');
        const data = res.data;
        const theme = data.default_theme || '';
        const newSettings = {
          defaultTheme: theme,
          iconUpdatedAt: data.icon_updated_at,
          shortName: data.short_name || 'Kotityöt',
          requireTaskConfirm: data.require_task_confirm || false,
          features: appSettings.features,
          loaded: true,
        };
        setAppSettings(prev => ({ ...newSettings, features: prev.features }));
        setAppDefaultTheme(theme);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
      } catch {
        setAppSettings(prev => ({ ...prev, loaded: true }));
      }
    };

    const fetchFeatures = async () => {
      try {
        const res = await api.get('/economy/config');
        const featureRows = res.data.filter(r => r.category === 'features');
        const features = { ...DEFAULT_FEATURES };
        for (const row of featureRows) {
          const key = row.config_key.replace('feature_', '');
          features[key] = row.config_value !== '0';
        }
        setAppSettings(prev => {
          const updated = { ...prev, features };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          return updated;
        });
      } catch {}
    };

    fetchSettings();
    fetchFeatures();
  }, []);

  const updateDefaultTheme = (theme) => {
    setAppSettings(prev => {
      const updated = { ...prev, defaultTheme: theme };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
    setAppDefaultTheme(theme);
  };

  const updateIconTimestamp = (timestamp) => {
    setAppSettings(prev => {
      const updated = { ...prev, iconUpdatedAt: timestamp };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const updateRequireTaskConfirm = (value) => {
    setAppSettings(prev => {
      const updated = { ...prev, requireTaskConfirm: value };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const setFeature = async (key, enabled) => {
    const configKey = key.startsWith('feature_') ? key : `feature_${key}`;
    const shortKey = configKey.replace('feature_', '');
    setAppSettings(prev => {
      const updated = { ...prev, features: { ...prev.features, [shortKey]: enabled } };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
    try {
      await api.put(`/economy/config/${configKey}`, { value: enabled ? '1' : '0' });
    } catch {
      setAppSettings(prev => {
        const updated = { ...prev, features: { ...prev.features, [shortKey]: !enabled } };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
      throw new Error('Tallennus epäonnistui');
    }
  };

  return (
    <AppSettingsContext.Provider value={{ ...appSettings, updateDefaultTheme, updateIconTimestamp, updateRequireTaskConfirm, setFeature }}>
      {children}
    </AppSettingsContext.Provider>
  );
};
